"use client";

import { Link } from "@/i18n/navigation";
import { PhoneIcon, ArrowRightIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import CtaLink from "@/components/ui/CtaLink";
import Contact from "@/components/Contact/Contact";

const PHONE_TEL = "+32493966507";

const CONTENT = {
  fr: {
    h1: "Vide maison gratuit à Bruxelles : ce qui est vrai, et ce qui ne l'est pas",
    heroSubtitle:
      "Non, un vide maison n'est pas gratuit par défaut. Mais dans certains cas, la valeur des objets présents peut couvrir tout ou partie du coût. Voici comment le savoir, honnêtement.",
    ctaCall: "Appeler maintenant",
    ctaQuote: "Demander un devis gratuit",
    intro:
      "La question revient souvent : peut-on faire vider une maison sans rien payer ? La réponse honnête est nuancée. Un vide maison mobilise du personnel, un véhicule et l'évacuation des déchets vers des filières adaptées : ces éléments ont un coût réel, quel que soit le bien concerné. Ce qui peut changer la donne, c'est la présence d'objets qui conservent une valeur marchande. Leur rachat vient alors se déduire du devis — parfois en partie, plus rarement en totalité. Cet article détaille dans quels cas c'est réellement possible, et dans quels cas il ne faut pas s'y attendre.",
    s1: {
      title: "D'où vient l'idée du « vide maison gratuit »",
      p: "L'expression circule beaucoup, en partie parce que certaines entreprises l'utilisent comme accroche publicitaire, et en partie parce que des situations réelles existent où le rachat d'objets a effectivement couvert l'ensemble des frais. Le problème n'est pas l'idée en elle-même, mais sa généralisation : présentée comme une règle systématique, elle ne correspond simplement pas à la réalité de la majorité des débarras. Un logement contient rarement, à lui seul, de quoi financer entièrement son propre vide maison.",
    },
    s2: {
      title: "Pourquoi un débarras a un coût réel, dans la plupart des cas",
      pBefore: "Vider un bien demande du temps de travail (tri, manutention, chargement), un transport adapté au volume, et le paiement des filières de traitement pour tout ce qui ne peut pas être revendu. Ces coûts existent que le logement contienne des objets de valeur ou non. C'est pour cette raison qu'un devis reste toujours établi au cas par cas plutôt que sur un forfait fixe — le détail des facteurs de prix est expliqué dans notre article ",
      pLink: "combien coûte un vide maison à Bruxelles",
      pAfter: ".",
    },
    s3: {
      title: "Le vrai levier : le rachat d'objets de valeur",
      pBefore: "Le mécanisme qui permet réellement de réduire, voire d'annuler une facture, c'est le rachat. Avant toute évacuation, nous identifions ce qui conserve une valeur marchande : mobilier ancien ou de style, argenterie, bijoux, objets d'art, collections (livres, vinyles, pièces), horlogerie. Leur estimation est ensuite déduite du montant du devis. Le fonctionnement complet de cette démarche est détaillé sur notre page ",
      pLink1: "rachat d'objets",
      pMid: ", ainsi que dans notre article ",
      pLink2: "rachat d'objets lors d'un vide maison",
      pMid2: ". Un objet qui paraît sans intérêt au premier regard peut parfois avoir une valeur réelle : mieux vaut nous le montrer que de trancher seul. Quelques repères pour s'y retrouver sont détaillés dans notre article ",
      pLink3: "comment reconnaître un objet qui vaut la peine d'être racheté",
      pAfter: ".",
    },
    s4: {
      title: "Dans quels cas le rachat peut couvrir tout, ou presque",
      intro: "Certaines situations se prêtent davantage à une réduction importante du coût, sans que cela constitue une garantie :",
      items: [
        "Le bien contient plusieurs meubles anciens, de style ou en matériaux nobles (bois massif, laiton, cristal)",
        "Des bijoux, de l'argenterie ou des objets d'art sont présents en quantité",
        "Une collection reconnue (livres rares, vinyles, pièces, timbres) est complète",
        "Le volume global à évacuer reste modéré par rapport à la valeur des objets identifiés",
      ],
      outro: "Même dans ces cas, la couverture totale du coût reste une possibilité et non une certitude : seule une estimation concrète du bien permet de le confirmer.",
    },
    s5: {
      title: "Dans quels cas il ne faut pas s'attendre à la gratuité",
      intro: "À l'inverse, certaines situations très fréquentes ne permettent pas de couvrir les frais, même partiellement :",
      items: [
        "Le logement contient surtout du mobilier récent d'entrée de gamme, sans valeur de revente",
        "Le volume à évacuer est important (cave, grenier, garage encombrés) sans objets valorisables en proportion",
        "Les objets sont fortement dégradés, humides ou infestés, ce qui exclut toute revente",
        "Il s'agit essentiellement d'encombrants, de déchets ou d'électroménager en fin de vie",
      ],
      outro: "Ce n'est pas un problème en soi : la majorité des vide maisons se déroulent ainsi, avec un devis payant classique. Mieux vaut le savoir dès le départ que découvrir une facture inattendue après une promesse trop optimiste.",
    },
    s6: {
      title: "Deux situations types, pour illustrer la différence",
      intro: "Ces deux exemples sont volontairement génériques, mais ils reflètent les deux extrémités les plus fréquentes.",
      case1Bold: "Un grenier et une cave à vider, sans lien avec une succession :",
      case1Rest: " cartons, vieux électroménager, meubles en aggloméré fatigués par l'humidité. Aucun de ces éléments n'a de valeur de revente. Le devis correspond alors simplement au temps de manutention, au transport et au traitement des déchets — sans aucune déduction possible.",
      case2Bold: "Une maison de famille à vider dans le cadre d'une succession :",
      case2Rest: " meubles de style transmis sur plusieurs générations, argenterie, quelques bijoux et une collection de livres anciens. Après estimation, la valeur rachetée peut couvrir une part importante, parfois la totalité, du coût du débarras — sans que cela soit garanti avant d'avoir vu les objets. Ce type de situation est aussi abordé dans notre article ",
      case2Link: "vide maison et succession à Bruxelles",
      case2After: ".",
      outro: "Entre ces deux extrêmes, la plupart des biens se situent quelque part au milieu : une réduction partielle, proportionnelle à ce qui a réellement de la valeur.",
    },
    s7: {
      title: "Comment se déroule concrètement une estimation",
      intro: "Pour rester honnête sur ce qui est réaliste ou non, l'estimation suit toujours les mêmes étapes, quel que soit le bien :",
      items: [
        "Premier contact : vous décrivez brièvement le bien (type, volume approximatif, présence éventuelle d'objets anciens ou de valeur)",
        "Photos ou visite : selon la situation, une première estimation se fait à partir de photos, ou directement sur place pour les biens plus complexes",
        "Repérage des objets valorisables : mobilier, bijoux, argenterie, collections sont identifiés et évalués séparément du reste",
        "Devis détaillé : le coût du débarras et la valeur rachetée apparaissent clairement, pour connaître le montant réel restant à charge",
      ],
      outro: "Cette transparence permet d'éviter deux écueils fréquents : une promesse de gratuité qui ne se vérifie jamais sur place, ou à l'inverse, une sous-estimation des objets qui prive le client d'une réduction à laquelle il avait droit. Le but est simplement de partir sur un chiffre qui correspond à la réalité du bien.",
    },
    s8: {
      title: "Ce qui aide à obtenir une estimation plus précise, plus vite",
      intro: "Quelques éléments simples permettent souvent d'obtenir une première estimation plus fiable dès le premier échange, sans attendre une visite complète :",
      items: [
        "Des photos claires et bien éclairées des pièces, prises d'ensemble et de plus près pour les objets qui semblent anciens ou de valeur",
        "Une mention des pièces difficiles d'accès (cave, grenier, sous-toit) pour anticiper le temps de manutention",
        "Les documents disponibles pour un objet particulier (facture, certificat, provenance familiale), s'ils existent",
        "Une indication du volume global (nombre de pièces, présence ou non d'un garage, d'une cave, d'un grenier)",
      ],
      outro: "Rien de tout cela n'est obligatoire : une estimation reste possible avec des informations minimales. Ces éléments permettent simplement de gagner du temps et d'affiner le premier chiffre communiqué.",
    },
    s9: {
      title: "Comment savoir ce qui est réaliste dans votre cas",
      pBefore: "La seule manière fiable de savoir où se situe votre situation entre ces deux extrêmes, c'est une estimation concrète. Nous pouvons l'établir lors d'une visite du bien, ou dans un premier temps à partir de photos et d'une description des pièces et objets présents. Cette estimation reste gratuite et sans engagement : elle sert uniquement à vous donner un chiffre réaliste, à la hausse comme à la baisse, plutôt qu'une promesse générique. Cela vaut aussi bien pour un ",
      pLink1: "débarras de maison",
      pMid: " complet que pour un ",
      pLink2: "débarras d'appartement",
      pAfter: " plus ciblé, ou pour une seule pièce comme une cave ou un grenier.",
      cta: "Demander une estimation gratuite",
    },
    s10: {
      title: "Se méfier des promesses de gratuité systématique",
      pBefore: "Une entreprise qui annonce un débarras « toujours gratuit », sans avoir vu le bien, prend un engagement qu'elle ne peut pas tenir dans tous les cas. Dans les faits, soit elle réajuste le prix une fois sur place — ce qui revient à une mauvaise surprise le jour du chantier — soit elle facture ailleurs ce qu'elle annonce gratuit ailleurs. Une estimation honnête, faite avant l'intervention et détaillée dans un devis clair, protège des deux. Nos ",
      pLink: "tarifs",
      pAfter: " sont établis sur cette base : un prix qui reflète le volume, l'accès et la valeur réelle des objets rachetés, communiqué avant toute intervention.",
    },
    retenirTitle: "À retenir",
    retenir: [
      "Un vide maison n'est jamais gratuit \"par principe\" : la main-d'œuvre, le transport et l'évacuation des déchets ont un coût réel.",
      "Ce qui peut réellement faire baisser la facture, voire l'annuler dans certains cas, c'est la valeur des objets rachetés lors du débarras.",
      "Plus le bien contient de meubles anciens, de bijoux ou de collections, plus la part couverte par le rachat peut être importante.",
      "À l'inverse, un logement au volume important mais sans objets de valeur reste facturé normalement.",
      "Seule une estimation sur place ou par photos permet de savoir ce qui est réaliste dans votre situation précise.",
    ],
    faqTitle: "Questions fréquentes",
    faq: [
      {
        q: "Une entreprise qui annonce un \"vide maison 100% gratuit\", est-ce fiable ?",
        a: "Cela dépend entièrement du contenu du bien : aucune entreprise sérieuse ne peut garantir la gratuité avant d'avoir vu ce qu'il y a à l'intérieur. Une promesse de gratuité systématique, sans visite ni estimation préalable, doit plutôt inciter à la prudence. Nous préférons annoncer une estimation honnête après avoir examiné la situation, plutôt qu'une promesse impossible à tenir dans tous les cas.",
      },
      {
        q: "Comment savoir si mes objets ont assez de valeur pour couvrir les frais ?",
        a: "Il n'y a pas de règle générale : un meuble ancien, un lot de bijoux ou une collection complète peuvent représenter une valeur significative, tandis qu'un mobilier récent d'entrée de gamme n'en a généralement aucune. La seule façon fiable de le savoir est de nous montrer les objets, sur place ou en photos, pour une estimation gratuite et sans engagement.",
      },
      {
        q: "Que se passe-t-il si la valeur des objets ne couvre pas tout le coût ?",
        a: "C'est la situation la plus fréquente : la valeur estimée des objets rachetés est simplement déduite du montant du devis, qui reste dans tous les cas gratuit à établir. Vous savez donc avant toute intervention ce qu'il reste réellement à votre charge, sans mauvaise surprise le jour du chantier.",
      },
      {
        q: "Faut-il un volume important pour espérer une réduction significative ?",
        a: "Non, ce n'est pas le volume qui compte mais la nature des objets présents. Un petit appartement contenant quelques pièces de valeur peut bénéficier d'une réduction plus importante qu'une grande maison remplie de mobilier sans intérêt marchand. Chaque bien est donc évalué individuellement.",
      },
      {
        q: "L'estimation est-elle payante, ou faut-il déjà s'engager pour l'obtenir ?",
        a: "L'estimation est gratuite et sans engagement, que vous acceptiez ou non notre proposition ensuite. Nous pouvons l'établir lors d'une visite sur place, ou dans un premier temps à partir de photos et d'une description du bien, ce qui permet souvent de se faire une première idée rapidement.",
      },
      {
        q: "Que devient ce qui n'a aucune valeur marchande, comme les vieux encombrants ?",
        a: "Les objets sans valeur de revente sont triés, puis évacués et orientés vers les filières de recyclage ou d'élimination adaptées. Ce tri fait partie intégrante du travail : rien n'est jeté en vrac sans distinction entre ce qui peut être valorisé et ce qui ne le peut pas.",
      },
      {
        q: "Peut-on obtenir une première idée avant même une visite du bien ?",
        a: "Oui, dans une certaine mesure. Des photos claires des pièces et, si possible, des objets qui semblent avoir de la valeur permettent souvent de donner une première estimation approximative. Une visite ou des photos plus complètes restent toutefois nécessaires pour un devis précis et définitif.",
      },
      {
        q: "L'accès au logement (étage, ascenseur) influence-t-il la part gratuite ?",
        a: "Indirectement, oui. L'accès joue sur le coût du débarras lui-même (temps de manutention, stationnement), mais pas sur la valeur des objets rachetés. Un bien difficile d'accès peut donc nécessiter davantage d'objets valorisables pour atteindre le même niveau de réduction qu'un bien facilement accessible.",
      },
    ],
    finalText: "Vous voulez savoir ce qu'il en est réellement pour votre bien ? Décrivez-nous la situation, avec ou sans photos, et nous vous donnons une estimation honnête, gratuite et sans engagement.",
  },
  nl: {
    h1: "Gratis huis leegmaken in Brussel: wat waar is, en wat niet",
    heroSubtitle:
      "Nee, een huis leegmaken is niet standaard gratis. Maar in bepaalde gevallen kan de waarde van de aanwezige voorwerpen de kostprijs geheel of gedeeltelijk dekken. Hier leest u hoe u dit eerlijk kunt inschatten.",
    ctaCall: "Bel nu",
    ctaQuote: "Vraag een gratis offerte aan",
    intro:
      "De vraag komt vaak terug: kan een huis leeggemaakt worden zonder iets te betalen? Het eerlijke antwoord is genuanceerd. Een huis leegmaken vergt personeel, een voertuig en de afvoer van afval naar gepaste kanalen: deze elementen hebben een reële kostprijs, ongeacht het betrokken pand. Wat de zaken kan veranderen, is de aanwezigheid van voorwerpen die nog marktwaarde hebben. Hun inkoop wordt dan van de offerte afgetrokken — soms gedeeltelijk, zelden volledig. Dit artikel beschrijft in welke gevallen dit werkelijk mogelijk is, en in welke gevallen u er niet op mag rekenen.",
    s1: {
      title: "Waar komt het idee van het “gratis huis leegmaken” vandaan",
      p: "De uitdrukking circuleert veel, deels omdat sommige bedrijven ze als publicitaire slogan gebruiken, en deels omdat er werkelijk situaties bestaan waarin de inkoop van voorwerpen daadwerkelijk alle kosten heeft gedekt. Het probleem is niet het idee op zich, maar de veralgemening ervan: voorgesteld als een systematische regel, komt het simpelweg niet overeen met de realiteit van de meeste ontruimingen. Een woning bevat zelden op zichzelf genoeg om haar eigen huis leegmaken volledig te financieren.",
    },
    s2: {
      title: "Waarom een ontruiming in de meeste gevallen een reële kostprijs heeft",
      pBefore: "Een pand leegmaken vergt werktijd (sortering, behandeling, laden), een aan het volume aangepast transport, en de betaling van verwerkingskanalen voor alles wat niet wederverkocht kan worden. Deze kosten bestaan ongeacht of de woning waardevolle voorwerpen bevat of niet. Daarom wordt een offerte altijd geval per geval opgesteld in plaats van op basis van een vast forfait — de details van de prijsfactoren worden uitgelegd in ons artikel ",
      pLink: "hoeveel kost een huis leegmaken in Brussel",
      pAfter: ".",
    },
    s3: {
      title: "De echte hefboom: de inkoop van waardevolle voorwerpen",
      pBefore: "Het mechanisme dat een factuur werkelijk kan verlagen, of zelfs annuleren, is de inkoop. Vóór elke afvoer identificeren we wat nog marktwaarde heeft: antiek of stijlmeubilair, zilverwerk, juwelen, kunstvoorwerpen, verzamelingen (boeken, vinylplaten, munten), uurwerken. Hun schatting wordt vervolgens van het offertebedrag afgetrokken. De volledige werking van deze aanpak wordt beschreven op onze pagina ",
      pLink1: "inkoop van voorwerpen",
      pMid: ", en ook in ons artikel ",
      pLink2: "inkoop van voorwerpen bij een huis leegmaken",
      pMid2: ". Een voorwerp dat op het eerste gezicht oninteressant lijkt, kan soms een echte waarde hebben: toon het ons liever dan zelf te beslissen. Enkele richtlijnen om uw weg te vinden worden beschreven in ons artikel ",
      pLink3: "hoe een voorwerp herkennen dat het waard is om ingekocht te worden",
      pAfter: ".",
    },
    s4: {
      title: "In welke gevallen kan de inkoop alles, of bijna alles, dekken",
      intro: "Bepaalde situaties lenen zich meer tot een aanzienlijke kostenverlaging, zonder dat dit een garantie inhoudt:",
      items: [
        "Het pand bevat meerdere antieke, stijl- of edelmaterialen meubelstukken (massief hout, messing, kristal)",
        "Er zijn juwelen, zilverwerk of kunstvoorwerpen in aanzienlijke hoeveelheid aanwezig",
        "Een erkende verzameling (zeldzame boeken, vinylplaten, munten, postzegels) is compleet",
        "Het totale af te voeren volume blijft beperkt in verhouding tot de waarde van de geïdentificeerde voorwerpen",
      ],
      outro: "Zelfs in deze gevallen blijft de volledige dekking van de kostprijs een mogelijkheid en geen zekerheid: enkel een concrete schatting van het pand laat toe dit te bevestigen.",
    },
    s5: {
      title: "In welke gevallen mag u niet op gratis rekenen",
      intro: "Omgekeerd zijn er zeer veelvoorkomende situaties waarin de kosten niet gedekt kunnen worden, zelfs niet gedeeltelijk:",
      items: [
        "De woning bevat vooral recent instapmeubilair, zonder wederverkoopwaarde",
        "Het af te voeren volume is aanzienlijk (volgeladen kelder, zolder, garage) zonder evenredig waardevolle voorwerpen",
        "De voorwerpen zijn sterk beschadigd, vochtig of aangetast, wat elke wederverkoop uitsluit",
        "Het gaat voornamelijk om grofvuil, afval of afgeschreven huishoudtoestellen",
      ],
      outro: "Dit is op zich geen probleem: de meerderheid van de huis leegmakingen verloopt op deze manier, met een klassieke betalende offerte. Beter is het om dit vanaf het begin te weten dan een onverwachte factuur te ontdekken na een te optimistische belofte.",
    },
    s6: {
      title: "Twee typische situaties ter illustratie van het verschil",
      intro: "Deze twee voorbeelden zijn bewust algemeen, maar weerspiegelen de twee meest voorkomende uitersten.",
      case1Bold: "Een zolder en een kelder te ledigen, zonder verband met een nalatenschap:",
      case1Rest: " dozen, oude huishoudtoestellen, spaanplaten meubels aangetast door vocht. Geen van deze elementen heeft wederverkoopwaarde. De offerte komt dan gewoon overeen met de behandelingstijd, het transport en de afvalverwerking — zonder enige mogelijke aftrek.",
      case2Bold: "Een familiehuis te ledigen in het kader van een nalatenschap:",
      case2Rest: " stijlmeubels doorgegeven over meerdere generaties, zilverwerk, enkele juwelen en een verzameling oude boeken. Na schatting kan de ingekochte waarde een aanzienlijk deel, soms het geheel, van de ontruimingskosten dekken — zonder dat dit gegarandeerd is vóór de voorwerpen gezien zijn. Dit type situatie wordt ook behandeld in ons artikel ",
      case2Link: "huis leegmaken en nalatenschap in Brussel",
      case2After: ".",
      outro: "Tussen deze twee uitersten bevindt de meerderheid van de panden zich ergens in het midden: een gedeeltelijke vermindering, evenredig aan wat werkelijk waarde heeft.",
    },
    s7: {
      title: "Hoe verloopt een schatting concreet",
      intro: "Om eerlijk te blijven over wat realistisch is of niet, volgt de schatting steeds dezelfde stappen, ongeacht het pand:",
      items: [
        "Eerste contact: u beschrijft kort het pand (type, geschat volume, eventuele aanwezigheid van antieke of waardevolle voorwerpen)",
        "Foto's of bezoek: afhankelijk van de situatie gebeurt een eerste schatting op basis van foto's, of rechtstreeks ter plaatse voor complexere panden",
        "Opsporing van waardevolle voorwerpen: meubilair, juwelen, zilverwerk, verzamelingen worden geïdentificeerd en apart van de rest geëvalueerd",
        "Gedetailleerde offerte: de kostprijs van de ontruiming en de ingekochte waarde worden duidelijk vermeld, zodat u het werkelijke bedrag kent dat u nog verschuldigd bent",
      ],
      outro: "Deze transparantie voorkomt twee veelvoorkomende valkuilen: een belofte van gratis die ter plaatse nooit wordt waargemaakt, of omgekeerd, een onderschatting van de voorwerpen die de klant een terechte korting ontzegt. Het doel is eenvoudigweg om te vertrekken van een cijfer dat overeenkomt met de realiteit van het pand.",
    },
    s8: {
      title: "Wat helpt om sneller een nauwkeurigere schatting te krijgen",
      intro: "Enkele eenvoudige elementen maken het vaak mogelijk om al bij het eerste contact een betrouwbaardere eerste schatting te krijgen, zonder op een volledig bezoek te wachten:",
      items: [
        "Duidelijke en goed belichte foto's van de kamers, zowel algemeen als van dichterbij voor voorwerpen die oud of waardevol lijken",
        "Een vermelding van moeilijk toegankelijke ruimtes (kelder, zolder, onder het dak) om de behandelingstijd te kunnen inschatten",
        "De beschikbare documenten voor een specifiek voorwerp (factuur, certificaat, familiale herkomst), indien aanwezig",
        "Een indicatie van het totale volume (aantal kamers, al dan niet aanwezigheid van een garage, kelder of zolder)",
      ],
      outro: "Niets hiervan is verplicht: een schatting blijft mogelijk met minimale informatie. Deze elementen laten enkel toe tijd te winnen en het eerste meegedeelde cijfer te verfijnen.",
    },
    s9: {
      title: "Hoe weten wat realistisch is in uw geval",
      pBefore: "De enige betrouwbare manier om te weten waar uw situatie zich tussen deze twee uitersten bevindt, is een concrete schatting. Wij kunnen deze opstellen tijdens een bezoek aan het pand, of in eerste instantie op basis van foto's en een beschrijving van de aanwezige kamers en voorwerpen. Deze schatting blijft gratis en vrijblijvend: ze dient enkel om u een realistisch cijfer te geven, zowel naar boven als naar beneden, in plaats van een algemene belofte. Dit geldt zowel voor een volledig ",
      pLink1: "huis leegmaken",
      pMid: " als voor een meer gerichte ",
      pLink2: "appartement leegmaken",
      pAfter: ", of voor één enkele ruimte zoals een kelder of een zolder.",
      cta: "Vraag een gratis schatting aan",
    },
    s10: {
      title: "Wees op uw hoede voor beloftes van systematische gratis dienstverlening",
      pBefore: "Een bedrijf dat een “altijd gratis” ontruiming aankondigt zonder het pand gezien te hebben, gaat een engagement aan dat het niet in alle gevallen kan nakomen. In de praktijk past het ofwel de prijs aan eens ter plaatse — wat neerkomt op een onaangename verrassing op de dag van de werf — ofwel factureert het elders wat het gratis aankondigt. Een eerlijke schatting, gemaakt vóór de interventie en gedetailleerd in een duidelijke offerte, beschermt tegen beide. Onze ",
      pLink: "tarieven",
      pAfter: " zijn op deze basis opgesteld: een prijs die het volume, de toegang en de werkelijke waarde van de ingekochte voorwerpen weerspiegelt, meegedeeld vóór elke interventie.",
    },
    retenirTitle: "Wat u moet onthouden",
    retenir: [
      "Een huis leegmaken is nooit gratis “als principe”: arbeid, transport en afvalverwerking hebben een reële kostprijs.",
      "Wat de factuur werkelijk kan verlagen, of in bepaalde gevallen zelfs annuleren, is de waarde van de tijdens de ontruiming ingekochte voorwerpen.",
      "Hoe meer het pand antiek meubilair, juwelen of verzamelingen bevat, hoe groter het door de inkoop gedekte deel kan zijn.",
      "Omgekeerd wordt een woning met een groot volume maar zonder waardevolle voorwerpen gewoon normaal gefactureerd.",
      "Enkel een schatting ter plaatse of aan de hand van foto's laat toe te weten wat realistisch is in uw specifieke situatie.",
    ],
    faqTitle: "Veelgestelde vragen",
    faq: [
      {
        q: "Is een bedrijf dat een “100% gratis huis leegmaken” aankondigt betrouwbaar?",
        a: "Dit hangt volledig af van de inhoud van het pand: geen enkel ernstig bedrijf kan gratis dienstverlening garanderen vóór het gezien heeft wat er zich binnenin bevindt. Een systematische belofte van gratis, zonder bezoek of voorafgaande schatting, moet eerder tot voorzichtigheid aanzetten. Wij geven liever een eerlijke schatting na het onderzoeken van de situatie, dan een belofte die niet in alle gevallen kan worden nagekomen.",
      },
      {
        q: "Hoe weet ik of mijn voorwerpen voldoende waarde hebben om de kosten te dekken?",
        a: "Er is geen algemene regel: een antiek meubelstuk, een partij juwelen of een complete verzameling kunnen een aanzienlijke waarde vertegenwoordigen, terwijl recent instapmeubilair doorgaans geen enkele waarde heeft. De enige betrouwbare manier om dit te weten, is ons de voorwerpen te tonen, ter plaatse of via foto's, voor een gratis en vrijblijvende schatting.",
      },
      {
        q: "Wat gebeurt er als de waarde van de voorwerpen niet alle kosten dekt?",
        a: "Dit is de meest voorkomende situatie: de geschatte waarde van de ingekochte voorwerpen wordt eenvoudigweg afgetrokken van het offertebedrag, dat in alle gevallen gratis blijft om op te stellen. U weet dus vóór elke interventie wat werkelijk voor uw rekening blijft, zonder onaangename verrassing op de dag van de werf.",
      },
      {
        q: "Is een groot volume nodig om op een aanzienlijke korting te mogen hopen?",
        a: "Nee, niet het volume telt maar de aard van de aanwezige voorwerpen. Een klein appartement met enkele waardevolle stukken kan een grotere korting genieten dan een groot huis vol met meubilair zonder marktwaarde. Elk pand wordt dus individueel geëvalueerd.",
      },
      {
        q: "Is de schatting betalend, of moet ik mij al engageren om ze te krijgen?",
        a: "De schatting is gratis en vrijblijvend, of u ons voorstel achteraf aanvaardt of niet. Wij kunnen ze opstellen tijdens een bezoek ter plaatse, of in eerste instantie op basis van foto's en een beschrijving van het pand, wat vaak toelaat om snel een eerste idee te krijgen.",
      },
      {
        q: "Wat gebeurt er met voorwerpen zonder marktwaarde, zoals oud grofvuil?",
        a: "Voorwerpen zonder wederverkoopwaarde worden gesorteerd, vervolgens afgevoerd en naar de gepaste recyclage- of verwerkingskanalen geleid. Deze sortering maakt integraal deel uit van het werk: niets wordt zomaar weggegooid zonder onderscheid tussen wat gevaloriseerd kan worden en wat niet.",
      },
      {
        q: "Kan ik al vóór een bezoek aan het pand een eerste idee krijgen?",
        a: "Ja, in zekere mate. Duidelijke foto's van de kamers en, indien mogelijk, van voorwerpen die waarde lijken te hebben, laten vaak toe om een eerste benaderende schatting te geven. Een bezoek of volledigere foto's blijven echter noodzakelijk voor een nauwkeurige en definitieve offerte.",
      },
      {
        q: "Beïnvloedt de toegang tot de woning (verdieping, lift) het gratis gedeelte?",
        a: "Onrechtstreeks wel. De toegang beïnvloedt de kostprijs van de ontruiming zelf (behandelingstijd, parkeren), maar niet de waarde van de ingekochte voorwerpen. Een moeilijk toegankelijk pand kan dus meer waardevolle voorwerpen vereisen om hetzelfde kortingsniveau te bereiken als een gemakkelijk toegankelijk pand.",
      },
    ],
    finalText: "Wilt u weten hoe het er werkelijk voor staat met uw pand? Beschrijf ons de situatie, met of zonder foto's, en wij geven u een eerlijke, gratis en vrijblijvende schatting.",
  },
  en: {
    h1: "Free house clearance in Brussels: what's true, and what isn't",
    heroSubtitle:
      "No, a house clearance isn't free by default. But in certain cases, the value of the items present can cover all or part of the cost. Here's how to find out, honestly.",
    ctaCall: "Call now",
    ctaQuote: "Request a free quote",
    intro:
      "The question comes up often: can a house be cleared without paying anything? The honest answer is nuanced. A house clearance requires staff, a vehicle and the removal of waste to appropriate channels: these have a real cost, whatever the property involved. What can change things is the presence of items that retain market value. Their buyback is then deducted from the quote — sometimes partially, more rarely in full. This article details in which cases this is genuinely possible, and in which cases you shouldn't expect it.",
    s1: {
      title: "Where the idea of a “free house clearance” comes from",
      p: "The expression circulates a lot, partly because some companies use it as an advertising hook, and partly because real situations do exist where the buyback of items has actually covered the entire cost. The problem isn't the idea itself, but its generalisation: presented as a systematic rule, it simply doesn't match the reality of most clearances. A home rarely contains, on its own, enough to fully fund its own clearance.",
    },
    s2: {
      title: "Why a clearance has a real cost, in most cases",
      pBefore: "Clearing a property requires working time (sorting, handling, loading), transport suited to the volume, and payment for processing anything that can't be resold. These costs exist whether or not the home contains valuable items. This is why a quote is always drawn up on a case-by-case basis rather than as a fixed package — the pricing factors are detailed in our article on ",
      pLink: "how much a house clearance costs in Brussels",
      pAfter: ".",
    },
    s3: {
      title: "The real lever: buying back valuable items",
      pBefore: "The mechanism that can genuinely reduce, or even cancel, a bill is buyback. Before any removal, we identify what retains market value: antique or period furniture, silverware, jewellery, art objects, collections (books, vinyl records, coins), clocks and watches. Their estimate is then deducted from the quoted amount. The full workings of this process are detailed on our ",
      pLink1: "item buyback",
      pMid: " page, as well as in our article ",
      pLink2: "buying back items during a house clearance",
      pMid2: ". An item that seems uninteresting at first glance can sometimes have real value: better to show it to us than decide alone. A few pointers to help you along are detailed in our article ",
      pLink3: "how to recognise an item worth buying back",
      pAfter: ".",
    },
    s4: {
      title: "When buyback can cover all, or almost all, of the cost",
      intro: "Certain situations are more conducive to a significant cost reduction, without this being a guarantee:",
      items: [
        "The property contains several antique, period or fine-material pieces of furniture (solid wood, brass, crystal)",
        "Jewellery, silverware or art objects are present in quantity",
        "A recognised collection (rare books, vinyl records, coins, stamps) is complete",
        "The overall volume to be removed remains modest relative to the value of the identified items",
      ],
      outro: "Even in these cases, full cost coverage remains a possibility rather than a certainty: only a concrete estimate of the property can confirm it.",
    },
    s5: {
      title: "When you shouldn't expect it to be free",
      intro: "Conversely, certain very common situations don't allow the costs to be covered, even partially:",
      items: [
        "The home mainly contains recent entry-level furniture, with no resale value",
        "The volume to be removed is large (cluttered cellar, attic, garage) without proportionally valuable items",
        "The items are heavily damaged, damp or infested, ruling out any resale",
        "It's essentially bulky items, waste or end-of-life appliances",
      ],
      outro: "This isn't a problem in itself: most house clearances go this way, with a standard paid quote. It's better to know this from the start than to discover an unexpected bill after an overly optimistic promise.",
    },
    s6: {
      title: "Two typical situations, to illustrate the difference",
      intro: "These two examples are deliberately generic, but they reflect the two most common extremes.",
      case1Bold: "An attic and a cellar to clear, unrelated to an estate:",
      case1Rest: " boxes, old appliances, chipboard furniture worn down by damp. None of these items has resale value. The quote then simply corresponds to the handling time, transport and waste processing — with no deduction possible.",
      case2Bold: "A family home to clear as part of an estate settlement:",
      case2Rest: " period furniture passed down over several generations, silverware, a few pieces of jewellery and a collection of old books. After the estimate, the bought-back value can cover a significant share, sometimes the entire cost, of the clearance — though this isn't guaranteed until the items have been seen. This type of situation is also covered in our article ",
      case2Link: "house clearance and estate settlement in Brussels",
      case2After: ".",
      outro: "Between these two extremes, most properties fall somewhere in the middle: a partial reduction, proportional to what genuinely has value.",
    },
    s7: {
      title: "How an estimate actually works",
      intro: "To stay honest about what's realistic or not, the estimate always follows the same steps, whatever the property:",
      items: [
        "Initial contact: you briefly describe the property (type, approximate volume, any antique or valuable items present)",
        "Photos or visit: depending on the situation, a first estimate is made from photos, or directly on site for more complex properties",
        "Identifying valuable items: furniture, jewellery, silverware, collections are identified and appraised separately from the rest",
        "Detailed quote: the cost of the clearance and the bought-back value are clearly shown, so you know the actual amount remaining payable",
      ],
      outro: "This transparency avoids two common pitfalls: a promise of a free service that never holds up on site, or conversely, an underestimation of the items that denies the customer a discount they were entitled to. The goal is simply to start from a figure that matches the reality of the property.",
    },
    s8: {
      title: "What helps get a more precise estimate, faster",
      intro: "A few simple things often make it possible to get a more reliable first estimate from the very first exchange, without waiting for a full visit:",
      items: [
        "Clear, well-lit photos of the rooms, both wide shots and closer ones for items that look old or valuable",
        "A mention of hard-to-access spaces (cellar, attic, under the roof) to anticipate handling time",
        "Any available documents for a particular item (invoice, certificate, family provenance), if they exist",
        "An indication of the overall volume (number of rooms, presence or absence of a garage, cellar or attic)",
      ],
      outro: "None of this is compulsory: an estimate remains possible with minimal information. These elements simply help save time and refine the first figure given.",
    },
    s9: {
      title: "How to know what's realistic in your case",
      pBefore: "The only reliable way to know where your situation falls between these two extremes is a concrete estimate. We can draw this up during a visit to the property, or initially from photos and a description of the rooms and items present. This estimate remains free and comes with no obligation: it's only there to give you a realistic figure, whether higher or lower, rather than a generic promise. This applies just as much to a full ",
      pLink1: "house clearance",
      pMid: " as to a more targeted ",
      pLink2: "apartment clearance",
      pAfter: ", or to a single room such as a cellar or an attic.",
      cta: "Request a free estimate",
    },
    s10: {
      title: "Be wary of promises of systematic free service",
      pBefore: "A company that advertises an “always free” clearance without having seen the property is making a commitment it cannot keep in every case. In practice, it either adjusts the price once on site — which amounts to an unpleasant surprise on the day of the job — or charges elsewhere for what it advertises as free. An honest estimate, made before the intervention and detailed in a clear quote, protects against both. Our ",
      pLink: "prices",
      pAfter: " are set on this basis: a price that reflects the volume, the access and the real value of the bought-back items, communicated before any intervention.",
    },
    retenirTitle: "Key takeaways",
    retenir: [
      "A house clearance is never free \"as a rule\": labour, transport and waste removal have a real cost.",
      "What can genuinely lower the bill, or even cancel it out in certain cases, is the value of the items bought back during the clearance.",
      "The more antique furniture, jewellery or collections a property contains, the larger the share covered by buyback can be.",
      "Conversely, a home with a large volume but no valuable items is billed normally.",
      "Only an on-site or photo-based estimate can tell you what's realistic for your specific situation.",
    ],
    faqTitle: "Frequently asked questions",
    faq: [
      {
        q: "Is a company advertising a \"100% free house clearance\" reliable?",
        a: "This depends entirely on the property's contents: no serious company can guarantee a free service before seeing what's inside. A systematic promise of a free service, with no visit or prior estimate, should rather prompt caution. We prefer to give an honest estimate after examining the situation, rather than a promise that can't be kept in every case.",
      },
      {
        q: "How do I know if my items have enough value to cover the costs?",
        a: "There's no general rule: an antique piece of furniture, a batch of jewellery or a complete collection can represent significant value, while recent entry-level furniture generally has none. The only reliable way to find out is to show us the items, on site or in photos, for a free, no-obligation estimate.",
      },
      {
        q: "What happens if the value of the items doesn't cover the whole cost?",
        a: "This is the most common situation: the estimated value of the bought-back items is simply deducted from the quoted amount, which remains free to draw up in all cases. So you know before any intervention what actually remains payable, with no unpleasant surprise on the day of the job.",
      },
      {
        q: "Do you need a large volume to hope for a significant discount?",
        a: "No, it's not the volume that counts but the nature of the items present. A small apartment containing a few valuable pieces can benefit from a bigger discount than a large house full of furniture with no market value. Each property is therefore assessed individually.",
      },
      {
        q: "Is the estimate paid, or do I have to commit before getting one?",
        a: "The estimate is free and comes with no obligation, whether or not you accept our proposal afterwards. We can draw it up during an on-site visit, or initially from photos and a description of the property, which often gives a quick first idea.",
      },
      {
        q: "What happens to items with no market value, like old bulky items?",
        a: "Items with no resale value are sorted, then removed and directed to the appropriate recycling or disposal channels. This sorting is an integral part of the work: nothing is thrown out in bulk without distinguishing what can be valued from what can't.",
      },
      {
        q: "Can I get a first idea even before a visit to the property?",
        a: "Yes, to some extent. Clear photos of the rooms and, if possible, of items that look valuable, often allow us to give a first rough estimate. A visit or more complete photos remain necessary, however, for an accurate, final quote.",
      },
      {
        q: "Does access to the home (floor, lift) affect the free portion?",
        a: "Indirectly, yes. Access affects the cost of the clearance itself (handling time, parking), but not the value of the bought-back items. A property that's hard to access may therefore need more valuable items to reach the same level of discount as an easily accessible property.",
      },
    ],
    finalText: "Want to know where you really stand with your property? Describe the situation to us, with or without photos, and we'll give you an honest, free, no-obligation estimate.",
  },
};

export default function PageClient({ locale }) {
  const c = CONTENT[locale];

  return (
    <article>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: c.faq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }),
        }}
      />

      <section className="relative overflow-hidden bg-sand text-ink">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(900px 480px at 15% -10%, rgba(63,108,81,0.08), transparent 55%)" }} />
        <div className="relative mx-auto max-w-[900px] px-6 py-20 lg:px-10 lg:py-24">
          <h1 className="max-w-3xl font-serif text-4xl font-black leading-[1.1] md:text-5xl">
            {c.h1}
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-ink/70 leading-relaxed">
            {c.heroSubtitle}
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center gap-2 rounded-2xl bg-cta-700 px-7 py-3.5 font-semibold text-white shadow-cta transition hover:-translate-y-0.5 hover:bg-cta-800">
              <PhoneIcon className="h-5 w-5" />
              {c.ctaCall}
            </a>
            <a href="#contactus" className="inline-flex items-center gap-2 rounded-2xl border-2 border-ink/15 bg-white px-7 py-3.5 font-semibold text-ink transition hover:border-accent hover:text-accent">
              {c.ctaQuote}
              <ArrowRightIcon className="h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-[800px] px-6 lg:px-10 space-y-8">
          <div>
            <p className="leading-relaxed text-neutral-700">{c.intro}</p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s1.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s1.p}</p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s2.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s2.pBefore}
              <Link href="/blog/combien-coute-vide-maison-bruxelles" className="font-semibold text-accent underline underline-offset-4">
                {c.s2.pLink}
              </Link>
              {c.s2.pAfter}
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s3.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s3.pBefore}
              <Link href="/rachat-objets" className="font-semibold text-accent underline underline-offset-4">
                {c.s3.pLink1}
              </Link>
              {c.s3.pMid}
              <Link href="/blog/rachat-objets-vide-maison" className="font-semibold text-accent underline underline-offset-4">
                {c.s3.pLink2}
              </Link>
              {c.s3.pMid2}
              <Link href="/blog/reconnaitre-objet-valeur-rachat" className="font-semibold text-accent underline underline-offset-4">
                {c.s3.pLink3}
              </Link>
              {c.s3.pAfter}
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s4.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s4.intro}</p>
            <ul className="mt-4 space-y-2">
              {c.s4.items.map((item) => (
                <li key={item} className="flex items-start gap-2 text-neutral-700">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sage" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-4 leading-relaxed text-neutral-700">{c.s4.outro}</p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s5.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s5.intro}</p>
            <ul className="mt-4 space-y-2">
              {c.s5.items.map((item) => (
                <li key={item} className="flex items-start gap-2 text-neutral-700">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-cta-700" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-4 leading-relaxed text-neutral-700">{c.s5.outro}</p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s6.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s6.intro}</p>
            <p className="mt-3 leading-relaxed text-neutral-700">
              <span className="font-semibold text-ink">{c.s6.case1Bold}</span>
              {c.s6.case1Rest}
            </p>
            <p className="mt-3 leading-relaxed text-neutral-700">
              <span className="font-semibold text-ink">{c.s6.case2Bold}</span>
              {c.s6.case2Rest}
              <Link href="/blog/vide-maison-succession-bruxelles" className="font-semibold text-accent underline underline-offset-4">
                {c.s6.case2Link}
              </Link>
              {c.s6.case2After}
            </p>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s6.outro}</p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s7.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s7.intro}</p>
            <ul className="mt-4 space-y-2">
              {c.s7.items.map((item, i) => (
                <li key={item} className="flex items-start gap-3 text-neutral-700">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sage/10 text-sm font-bold text-sage">
                    {i + 1}
                  </span>
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-4 leading-relaxed text-neutral-700">{c.s7.outro}</p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s8.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s8.intro}</p>
            <ul className="mt-4 space-y-2">
              {c.s8.items.map((item) => (
                <li key={item} className="flex items-start gap-2 text-neutral-700">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sage" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-4 leading-relaxed text-neutral-700">{c.s8.outro}</p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s9.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s9.pBefore}
              <Link href="/debarras-maison" className="font-semibold text-accent underline underline-offset-4">
                {c.s9.pLink1}
              </Link>
              {c.s9.pMid}
              <Link href="/debarras-appartement" className="font-semibold text-accent underline underline-offset-4">
                {c.s9.pLink2}
              </Link>
              {c.s9.pAfter}
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
              <CtaLink href="#contactus" icon={ArrowRightIcon} variant="accent">
                {c.s9.cta}
              </CtaLink>
            </div>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s10.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s10.pBefore}
              <Link href="/tarifs" className="font-semibold text-accent underline underline-offset-4">
                {c.s10.pLink}
              </Link>
              {c.s10.pAfter}
            </p>
          </div>

          <div className="rounded-2xl border border-sand-line bg-sand p-6">
            <h2 className="font-serif text-xl font-extrabold text-brand-900">{c.retenirTitle}</h2>
            <ul className="mt-4 space-y-2.5">
              {c.retenir.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-neutral-700">
                  <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-sage" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.faqTitle}</h2>
            <div className="mt-4 space-y-4">
              {c.faq.map((item) => (
                <div key={item.q} className="rounded-2xl border border-sand-line bg-sand p-5">
                  <p className="font-bold text-ink">{item.q}</p>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-600">{item.a}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-sand-line bg-white p-6 text-center">
            <p className="leading-relaxed text-neutral-700">{c.finalText}</p>
            <div className="mt-5 flex flex-wrap justify-center gap-4">
              <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center gap-2 rounded-2xl bg-cta-700 px-7 py-3.5 font-semibold text-white shadow-cta transition hover:-translate-y-0.5 hover:bg-cta-800">
                <PhoneIcon className="h-5 w-5" />
                {c.ctaCall}
              </a>
              <a href="#contactus" className="inline-flex items-center gap-2 rounded-2xl border-2 border-ink/15 bg-white px-7 py-3.5 font-semibold text-ink transition hover:border-accent hover:text-accent">
                {c.ctaQuote}
                <ArrowRightIcon className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <Contact />
    </article>
  );
}
